import type { Discount } from "@/models/discount"
import type {
  BillingItem,
  BillingPreviewDiscountPayload,
  BillingPreviewV1CartItem,
  BillingPreviewWireModifierGroup,
} from "@/domain/cart/billing-preview.types"
import { resolveModifierQuantity } from "@/domain/cart/cart.transformer"
import { isRfc4122Uuid } from "@/shared/ids/client-item-id"

/**
 * Maps Redux/API discount selection to billing preview API discount rows.
 */
export function mapDiscountsToBillingPreviewPayload(
  discountsState: { selected?: Discount }
): BillingPreviewDiscountPayload[] {
  const d = discountsState.selected
  if (!d?.discount_id) return []
  return [
    {
      discount_id: String(d.discount_id),
      reason: d.description ?? "",
    },
  ]
}

/**
 * Strips internal-only fields and maps modifiers to the wire contract (child_menu_item_id + quantity only).
 * Applies parent line quantity when modifier quantity is null/undefined before send.
 */
export function mapPreviewLinesToBillingItems(rows: BillingPreviewV1CartItem[]): BillingItem[] {
  return rows.map((row) => {
    const parentQty = Math.max(1, Math.floor(Number(row.quantity)) || 1)
    const selected_modifiers: BillingPreviewWireModifierGroup[] = (row.selected_modifiers ?? [])
      .map((g) => ({
        modifier_group_id: String(g.modifier_group_id),
        items: (g.items ?? []).map((m) => {
          const qty = resolveModifierQuantity(m, parentQty)
          return {
            child_menu_item_id: String(m.child_menu_item_id ?? "").trim(),
            quantity: Math.max(1, Math.floor(Number(qty)) || 1),
          }
        }),
      }))
      .filter((g) => g.items.length > 0)

    const item: BillingItem = {
      client_item_id: String(row.client_item_id),
      menu_item_id: String(row.menu_item_id),
      quantity: parentQty,
      selected_modifiers,
    }
    if (row.combos && row.combos.length > 0) {
      item.combos = row.combos
    }
    return item
  })
}

/** Hard assertions immediately before HTTP — contract must be satisfied. */
export function assertBillingPreviewRequestPayload(items: BillingItem[]): void {
  if (!items.length) {
    throw new Error("[BILLING_PREVIEW_EMPTY] Billing preview requires at least one line item")
  }
  for (const it of items) {
    if (!isRfc4122Uuid(it.client_item_id)) {
      throw new Error(
        `[BILLING_PREVIEW_CLIENT_ID] client_item_id must be an RFC 4122 UUID (got ${String(it.client_item_id)})`
      )
    }
    const q = Math.floor(Number(it.quantity))
    if (!Number.isFinite(q) || q < 1) {
      throw new Error(
        `[BILLING_PREVIEW_INVALID_QTY] Each item must have quantity >= 1 (client_item_id=${it.client_item_id})`
      )
    }
    for (const g of it.selected_modifiers ?? []) {
      for (const m of g.items ?? []) {
        if (!String(m.child_menu_item_id ?? "").trim()) {
          throw new Error(
            `[BILLING_PREVIEW_MODIFIER_CHILD] child_menu_item_id is required (client_item_id=${it.client_item_id})`
          )
        }
        const mq = Math.floor(Number(m.quantity))
        if (!Number.isFinite(mq) || mq < 1) {
          throw new Error(
            `[BILLING_PREVIEW_MODIFIER_QTY] Modifier quantity must be >= 1 (client_item_id=${it.client_item_id})`
          )
        }
      }
    }
  }
}
